import { Component, computed, inject, Injectable, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
    FilterChip,
    Page,
    PageResult,
    QuerySortDirection,
    ReactiveSearchHomeComponent,
    SearchService,
    ServiceHandler,
} from '@nucleus/ui-base';
import { JobControllerService } from '../../api/scheduler-service/api/jobController.service';
import { JobRecord } from '../../api/scheduler-service/model/jobRecord';
import { JobFilter } from '../../api/scheduler-service/model/jobFilter';
import { PaginatedFilterRequestJobFilter } from '../../api/scheduler-service/model/paginatedFilterRequestJobFilter';
import { EnabledRequest } from '../../api/scheduler-service/model/enabledRequest';

/**
 * Schedules (jobs) list page — TASK-233. Replaces the coming-soon stub the
 * schedule-creation flow previously landed on. Lists {@link JobRecord} rows
 * from the scheduler-service via <code>POST /v1/jobs/filter</code> and offers
 * per-row lifecycle actions (view / enable-disable / delete) plus a header
 * "New schedule" action.
 *
 * <p>Migrated to <code>&lt;lib-list-page&gt;</code> + {@link ReactiveSearchHomeComponent}
 * — same shell as the capabilities registry — so the page inherits the
 * standard tasksense chrome (toolbar, omnibar, chips, drawer, paginator,
 * empty state). Unlike the capability screen the scheduler DOES expose a
 * server-side filter endpoint, so {@link JobSearchAdapter} forwards the
 * filter + page straight through and maps the {@code PageResultJobRecord}
 * envelope onto the lib {@link PageResult}.
 *
 * <p>Permission gates (per-screen `entity:action` caps, TASK-RBAC):
 *   screen-reach = {@code job:list}/{@code job:access}/{@code scheduler:view};
 *   new schedule = {@code job:create}; enable/disable = {@code job:update};
 *   delete = {@code job:delete}; view = {@code scheduler:view}.
 */

/** Tri-state enabled narrowing for the status filter drawer. */
type EnabledFilter = 'ALL' | 'ENABLED' | 'DISABLED';

/**
 * Filter envelope for the schedules list. {@code value} is the omnibar
 * free-text (matched server-side against name / task type); {@code enabled}
 * narrows to enabled-only or disabled-only (<code>'ALL'</code> = no
 * narrowing).
 */
export class ScheduleFilter {
    constructor(
        public value: string = '',
        public enabled: EnabledFilter = 'ALL',
    ) {}
}

/**
 * Adapter that lets {@link SchedulesComponent} extend
 * {@link ReactiveSearchHomeComponent} over the generated
 * {@link JobControllerService}. The scheduler-service exposes a real
 * server-side filter + pagination endpoint, so the adapter simply maps the
 * lib {@link Page}/{@link ScheduleFilter} onto the generated
 * {@link PaginatedFilterRequestJobFilter} and maps the response envelope
 * back onto the lib {@link PageResult}.
 */
@Injectable({ providedIn: 'root' })
export class JobSearchAdapter implements SearchService<JobRecord, ScheduleFilter> {

    private readonly api: JobControllerService = inject(JobControllerService);

    search(handler: ServiceHandler<JobRecord[]>): void {
        handler.onStart?.();
        this.api.filter(this.toRequest(new ScheduleFilter())).subscribe({
            next:     res => handler.onSuccess(res?.data ?? []),
            error:    err => handler.onError?.(err),
            complete: ()  => handler.onComplete?.(),
        });
    }

    filter(handler: ServiceHandler<PageResult<JobRecord>>, filter: ScheduleFilter, page?: Page): void {
        handler.onStart?.();
        this.api.filter(this.toRequest(filter, page)).subscribe({
            next: res => handler.onSuccess(new PageResult<JobRecord>(
                res?.data ?? [],
                res?.totalData ?? 0,
                res?.page ?? (page?.pageNumber ?? 0),
            )),
            error:    err => handler.onError?.(err),
            complete: ()  => handler.onComplete?.(),
        });
    }

    private toRequest(filter: ScheduleFilter, page?: Page): PaginatedFilterRequestJobFilter {
        const jobFilter: JobFilter = {
            value:   (filter.value ?? '').trim() || undefined,
            enabled: filter.enabled === 'ALL' ? undefined : filter.enabled === 'ENABLED',
        };
        return {
            filter: jobFilter,
            page: page ? {
                pageNumber:    page.pageNumber,
                pageSize:      page.pageSize,
                sortField:     page.sortField,
                sortDirection: page.sortDirection,
            } : undefined,
        };
    }
}

@Component({
    selector: 'app-schedules',
    templateUrl: './schedules.component.html',
    styleUrl: './schedules.component.css',
    standalone: false,
})
export class SchedulesComponent
    extends ReactiveSearchHomeComponent<JobRecord, ScheduleFilter>
    implements OnInit {

    private readonly jobService: JobControllerService = inject(JobControllerService);
    protected override router: Router = inject(Router);
    protected override dialog: MatDialog = inject(MatDialog);

    readonly enabledOptions: Array<{ value: EnabledFilter; label: string }> = [
        { value: 'ALL',      label: 'All' },
        { value: 'ENABLED',  label: 'Enabled' },
        { value: 'DISABLED', label: 'Disabled' },
    ];

    displayedColumns = ['name', 'taskType', 'capabilityVersion', 'enabled', 'createTimestamp', 'actions'];

    constructor() {
        super(inject(JobSearchAdapter), 'name', QuerySortDirection.ASC);
    }

    // ── ReactiveSearchHomeComponent overrides ───────────────────────────────

    override createFilter(): ScheduleFilter {
        return new ScheduleFilter();
    }

    override onResetFilter(): void {
        this.filter.update(f => Object.assign(f, new ScheduleFilter()));
    }

    /** Gate the auto-fire on permission so the base's ngAfterViewInit doesn't
     *  issue a request the user can't see (showAccessDeny will already have
     *  navigated away). */
    override isReadyForSearch(): boolean {
        return this.permissionChecker.hasAccessToViewSchedulingSchedules();
    }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToViewSchedulingSchedules()) {
            this.showAccessDeny();
        }
    }

    // ── filter wiring ────────────────────────────────────────────────────────

    setEnabledFilter(value: EnabledFilter): void {
        this.filter.update(f =>
            Object.assign(Object.create(Object.getPrototypeOf(f)), f, { enabled: value }));
    }

    activeFilterChips = computed<FilterChip[]>(() => {
        const f = this.filter();
        const chips: FilterChip[] = [];
        if (f.enabled && f.enabled !== 'ALL') {
            const label = this.enabledOptions.find(o => o.value === f.enabled)?.label ?? f.enabled;
            chips.push({ key: 'enabled', icon: 'toggle_on', label: `Status: ${label}`, removable: true });
        }
        return chips;
    });

    hasRemovableChips(): boolean {
        return this.activeFilterChips().some(c => c.removable);
    }

    removeFilterChip(key: string): void {
        if (key === 'enabled') this.setEnabledFilter('ALL');
    }

    // ── permission helpers ───────────────────────────────────────────────────

    canView(): boolean   { return this.permissionChecker.hasAccessToViewSchedulingSchedules(); }
    canCreate(): boolean { return this.permissionChecker.hasAccessToCreateSchedule(); }
    canUpdate(): boolean { return this.permissionChecker.hasAccessToUpdateSchedule(); }
    canDelete(): boolean { return this.permissionChecker.hasAccessToDeleteSchedule(); }

    statusClass(enabled: boolean | undefined): string {
        return enabled ? 'status-pill status-enabled' : 'status-pill status-disabled';
    }

    // ── actions ──────────────────────────────────────────────────────────────

    /** New schedule → the existing schedule-creation editor. */
    createSchedule(): void {
        if (!this.canCreate()) { this.showAccessDeny(); return; }
        this.router.navigate(['/scheduling/schedule']);
    }

    /** View / open a schedule in the editor, carrying its id. */
    openDetail(j: JobRecord): void {
        if (!this.canView()) { this.showAccessDeny(); return; }
        this.router.navigate(['/scheduling/schedule'], { queryParams: { jobId: j.id } });
    }

    /** Enable / disable toggle → PATCH /v1/jobs/{id}/enabled. */
    toggleEnabled(j: JobRecord): void {
        if (!this.canUpdate()) { this.showAccessDeny(); return; }
        const next = !j.enabled;
        const body: EnabledRequest = { enabled: next };
        this.jobService.setEnabled(j.id, body).subscribe({
            next:  () => { this.showSuccess(`${next ? 'Enabled' : 'Disabled'} ${j.name}`); this.refreshData(true); },
            error: (e) => { this.showError(e, next ? 'Enable failed' : 'Disable failed'); },
        });
    }

    /** Delete a schedule after confirmation → DELETE /v1/jobs/{id}. */
    deleteSchedule(j: JobRecord): void {
        if (!this.canDelete()) { this.showAccessDeny(); return; }
        this.confirm('Delete schedule', `Delete schedule "${j.name}"? This cannot be undone.`).then(ok => {
            if (!ok) return;
            if (!this.canDelete()) { this.showAccessDeny(); return; }
            this.jobService._delete(j.id).subscribe({
                next:  () => { this.showSuccess(`Deleted ${j.name}`); this.refreshData(true); },
                error: (e) => { this.showError(e, 'Delete failed'); },
            });
        });
    }
}
